import React, { useEffect, useState } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { Collapse, Pagination, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Loading from '../../../../common/Loading';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

function CommentsTable({ clinicid }) {
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [copiedItems, setCopiedItems] = useState({});

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    const pageSizeOptions = [5, 10, 20, 50];

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getComments = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const res = await clinicService.getReputationComments(clinicid, currentPage - 1, itemsPerPage, {
                    signal: controller.signal,
                });

                if (isMounted) {
                    setComments(res.data.content);
                    setTotalElements(res.data.totalElements);
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error('Fetch error:', err);
                    setError('Unable to load comments. Please try again.');
                    setComments([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getComments();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [clinicid, currentPage, itemsPerPage]);

    const toggleRow = (id) => {
        setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const copyToClipboard = (text, itemType, itemId) => {
        navigator.clipboard.writeText(text).then(() => {
            const key = `${itemType}-${itemId}`;
            setCopiedItems((prev) => ({ ...prev, [key]: true }));
            setTimeout(() => {
                setCopiedItems((prev) => ({ ...prev, [key]: false }));
            }, 2000);
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'SENT':
                return (
                    <span className="reputation-status-badge reputation-status-badge--sent">
                        <i className="bi bi-check-circle"></i> Sent
                    </span>
                );
            case 'PENDING':
                return (
                    <span className="reputation-status-badge reputation-status-badge--pending">
                        <i className="bi bi-clock"></i> Pending
                    </span>
                );
            case 'ERROR':
                return (
                    <span className="reputation-status-badge reputation-status-badge--error">
                        <i className="bi bi-x-circle"></i> Error
                    </span>
                );
            default:
                return <span className="reputation-status-badge reputation-status-badge--inactive">{status}</span>;
        }
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const truncateNotes = (text, length = 100) => {
        if (!text) return '—';
        return text.length > length ? `${text.substring(0, length)}…` : text;
    };

    const totalPages = Math.ceil(totalElements / itemsPerPage);
    const startItem = comments.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, totalElements);

    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
                {number}
            </Pagination.Item>
        );
    }

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-chat-text"></i>
                    Patient Comments
                    {!isLoading && (
                        <span className="text-muted small fw-normal">
                            ({totalElements} record{totalElements !== 1 ? 's' : ''})
                        </span>
                    )}
                </span>
            </div>

            <div className="notifications-panel__body">
                <div className="reputation-toolbar">
                    <div className="d-flex align-items-center gap-2">
                        <span>Show</span>
                        <select
                            className="form-select form-select-sm reputation-page-size"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isLoading}
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span>entries</span>
                    </div>
                    <div>
                        Total: <strong>{totalElements}</strong>
                    </div>
                </div>

                {error && !isLoading && (
                    <div className="alert alert-warning border-0 py-2 px-3 small mb-3">{error}</div>
                )}

                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0 process-queue-table reputation-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <i className="bi bi-person"></i> Patient
                                        </th>
                                        <th>
                                            <i className="bi bi-chat"></i> Notes
                                        </th>
                                        <th>
                                            <i className="bi bi-check-circle"></i> Status
                                        </th>
                                        <th>
                                            <i className="bi bi-calendar"></i> Date
                                        </th>
                                        <th className="text-end">
                                            <i className="bi bi-gear"></i> Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comments.length > 0 ? (
                                        comments.map((comment) => (
                                            <React.Fragment key={comment.id}>
                                                <tr className="reputation-row">
                                                    <td>
                                                        <span className="reputation-name">
                                                            {comment.firstName} {comment.lastName}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="reputation-notes-preview">{truncateNotes(comment.notes)}</span>
                                                    </td>
                                                    <td>{getStatusBadge(comment.status)}</td>
                                                    <td className="reputation-date">{formatDate(comment.createdAt)}</td>
                                                    <td className="text-end">
                                                        <ActionTooltipButton
                                                            tooltip={expandedRows[comment.id] ? 'Collapse details' : 'View details'}
                                                            tooltipId={`comment-expand-${comment.id}`}
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => toggleRow(comment.id)}
                                                            aria-controls={`collapse-comment-${comment.id}`}
                                                            aria-expanded={expandedRows[comment.id]}
                                                        >
                                                            <i className={`bi ${expandedRows[comment.id] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                        </ActionTooltipButton>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="5" className="p-0 border-0">
                                                        <Collapse in={expandedRows[comment.id]}>
                                                            <div id={`collapse-comment-${comment.id}`} className="reputation-detail-panel">
                                                                <div className="row g-3">
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Comment ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{comment.id}</span>
                                                                                <ActionTooltipButton
                                                                                    tooltip={copiedItems[`comment-${comment.id}`] ? 'Copied!' : 'Copy comment ID'}
                                                                                    tooltipId={`copy-comment-${comment.id}`}
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    className="clinic-copy-btn p-1"
                                                                                    onClick={() => copyToClipboard(comment.id, 'comment', comment.id)}
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`comment-${comment.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </ActionTooltipButton>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Created At</span>
                                                                            <span className="notifications-info-value">{formatDate(comment.createdAt)}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Patient ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{comment.patientId}</span>
                                                                                <ActionTooltipButton
                                                                                    tooltip={copiedItems[`patient-${comment.id}`] ? 'Copied!' : 'Copy patient ID'}
                                                                                    tooltipId={`copy-patient-${comment.id}`}
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    className="clinic-copy-btn p-1"
                                                                                    onClick={() => copyToClipboard(comment.patientId, 'patient', comment.id)}
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`patient-${comment.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </ActionTooltipButton>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Clinic ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{comment.clinicId}</span>
                                                                                <ActionTooltipButton
                                                                                    tooltip={copiedItems[`clinic-${comment.id}`] ? 'Copied!' : 'Copy clinic ID'}
                                                                                    tooltipId={`copy-clinic-${comment.id}`}
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    className="clinic-copy-btn p-1"
                                                                                    onClick={() => copyToClipboard(comment.clinicId, 'clinic', comment.id)}
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`clinic-${comment.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </ActionTooltipButton>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Status</span>
                                                                            <span className="notifications-info-value">{getStatusBadge(comment.status)}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-12">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Full Notes</span>
                                                                            <pre className="reputation-notes-full">{comment.notes || '—'}</pre>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="p-0 border-0">
                                                <div className="reputation-empty">
                                                    <i className="bi bi-chat-text"></i>
                                                    No patient comments found.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {comments.length > 0 && (
                            <div className="reputation-footer">
                                <div>
                                    Showing {startItem} to {endItem} of {totalElements} entries
                                </div>
                                {totalPages > 1 && (
                                    <Pagination className="mb-0">
                                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev
                                            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                            disabled={currentPage === 1}
                                        />
                                        {paginationItems}
                                        <Pagination.Next
                                            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default CommentsTable;
